import { jwtDecode } from 'jwt-decode';

type DecodedToken = {
  exp?: number;
  scope?: string[];
  name?: string;
  email?: string;
  tenantId?: number;
  isMaster?: boolean;
  tenantSlug?: string;
};

/**
 * Check if a JWT token is expired.
 * Returns true if token is expired, invalid, or missing.
 * Adds a small buffer (10 seconds) to account for clock skew and network latency.
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(token);

    if (!decoded.exp) {
      // Token without expiration claim is considered expired for safety
      return true;
    }

    // Add 10 second buffer to avoid edge cases where token expires during request
    const bufferSeconds = 10;
    const currentTimeSeconds = Math.floor(Date.now() / 1000);

    return decoded.exp <= currentTimeSeconds + bufferSeconds;
  } catch {
    // If token is malformed, treat as expired
    return true;
  }
}

/**
 * Get the auth token from localStorage, clearing it if expired.
 * Returns null if token is missing or expired.
 */
export function getValidAuthToken(): string | null {
  const token = localStorage.getItem('authToken');

  if (isTokenExpired(token)) {
    // Clear both tokens if access token is expired
    // The refresh flow should happen via trpcClient.ts authLink
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    return null;
  }

  return token;
}

/**
 * Decode a JWT token and return its payload.
 * Returns null if token is invalid.
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
}

/**
 * Clear all authentication-related data from localStorage.
 * This is safe to call even if localStorage is inaccessible.
 */
export function clearAuthData(): void {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
  } catch {
    // localStorage might be unavailable or corrupted, ignore
  }
}

/**
 * Validate that localStorage is accessible and contains valid data.
 * If corrupted auth data is detected, it will be cleared.
 * Returns true if localStorage is accessible.
 */
export function validateStorageHealth(): boolean {
  try {
    // Test localStorage access
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);

    // Check if auth token, if present, is at least parseable
    const token = localStorage.getItem('authToken');
    if (token) {
      // Try to decode - if it fails, token is corrupted
      const decoded = decodeToken(token);
      if (!decoded) {
        clearAuthData();
      }
    }

    return true;
  } catch {
    // localStorage is not accessible
    return false;
  }
}

/**
 * Clear all application data from localStorage and redirect to login.
 * Use this as a last resort when the app is in an unrecoverable state.
 */
export function resetAppState(): void {
  try {
    // Clear auth data
    clearAuthData();

    // Clear other app state that might be corrupted
    // Keep tenant-related data to preserve user's tenant context
    const keysToPreserve = ['darkMode'];
    const preservedValues: Record<string, string | null> = {};

    keysToPreserve.forEach((key) => {
      preservedValues[key] = localStorage.getItem(key);
    });

    localStorage.clear();

    keysToPreserve.forEach((key) => {
      const value = preservedValues[key];
      if (value) {
        localStorage.setItem(key, value);
      }
    });
  } catch {
    // If localStorage is completely broken, try to clear everything
    try {
      localStorage.clear();
    } catch {
      // Nothing more we can do
    }
  }
}
